import React, { useState, useRef } from 'react';
import { X, Mic, Loader2, ImagePlus, Trash2, Music } from 'lucide-react';
import { downscaleToDataUrl } from '../../lib/img';
import { toast } from '../../hooks/use-toast';

// Reads a File as a data: URL -- same shape the backend already accepts for
// every other reference (URL, data URI, or bare base64), just for audio
// instead of an image, so there's no separate upload round-trip needed.
function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const RESOLUTIONS = [
  { key: '480p', label: '480p · faster' },
  { key: '720p', label: '720p · slower' },
];

// A talking avatar clip, driven by an audio track -- not an animation of a
// still. Separate from VideoDialog because the input it needs (audio) is
// nothing that dialog collects, and nothing else in the video chain takes it.
// ~60s per tick (audio prep or one denoised segment) is a rough, admittedly
// approximate average from real measured runs -- shown as an estimate, not
// promised, because audio-prep time actually scales with the clip's length
// and this doesn't know that yet.
const SECONDS_PER_STEP_ESTIMATE = 60;

const STAGE_LABELS = {
  preparing_audio: 'Preparing audio',
  generating: 'Generating',
  denoising: 'Generating',
};

export default function AvatarVideoDialog({ open, onClose, onGenerate, busy, isAdmin, progress }) {
  // A business/quality-risk ceiling, not a technical one -- the model itself
  // has no hard cap. LongCat's own paper only validated identity consistency
  // up to 5 chained segments, so going well past that is a tradeoff the
  // admin tier is trusted to judge for itself. The backend enforces the same
  // split independently; this only keeps the slider honest about it.
  const maxSegments = isAdmin ? 100 : 20;
  const [prompt, setPrompt] = useState('a person speaking naturally to camera, warm lighting, casual UGC style');
  const [audioMode, setAudioMode] = useState('upload'); // 'upload' | 'tts'
  const [audioName, setAudioName] = useState('');
  const [audioDataUrl, setAudioDataUrl] = useState('');
  const [script, setScript] = useState('');
  const [image, setImage] = useState(null);
  const [numSegments, setNumSegments] = useState(1);
  const [resolution, setResolution] = useState('480p');
  const audioRef = useRef(null);
  const imageRef = useRef(null);

  if (!open) return null;

  const onAudioPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      setAudioDataUrl(await readAsDataUrl(f));
      setAudioName(f.name);
    } catch {
      toast({ title: 'Could not read that audio file' });
    }
  };

  const onImagePick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try { setImage(await downscaleToDataUrl(f, 1280)); } catch { /* ignore */ }
  };

  const canSubmit = audioMode === 'upload' ? !!audioDataUrl : !!script.trim();

  const submit = () => {
    if (!canSubmit) { toast({ title: audioMode === 'upload' ? 'An audio track is required' : 'Write what the avatar should say' }); return; }
    onGenerate({
      prompt,
      audio: audioMode === 'upload' ? audioDataUrl : null,
      script: audioMode === 'tts' ? script.trim() : null,
      image, numSegments, resolution,
    });
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-lg overflow-hidden rounded-2xl border border-[#2A2A30] bg-[#141417]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-[#2A2A30] px-5 py-3.5">
          <div className="flex items-center gap-2">
            <Mic size={16} className="text-node-model" />
            <span className="font-medium text-white">Create avatar video</span>
          </div>
          <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-md text-white/50 hover:bg-white/5 hover:text-white"><X size={16} /></button>
        </div>

        <div className="space-y-4 p-5">
          <div>
            <label className="text-[11px] font-medium uppercase tracking-wider text-white/50">Voice (required)</label>
            <div className="mt-1.5 flex gap-1.5">
              <button onClick={() => setAudioMode('upload')} data-testid="avatar-mode-upload"
                className={`flex-1 rounded-md border px-2 py-1.5 text-xs transition-colors ${audioMode === 'upload' ? 'border-white bg-white text-black' : 'border-[#2A2A30] text-white/60 hover:text-white'}`}>
                Upload audio
              </button>
              <button onClick={() => setAudioMode('tts')} data-testid="avatar-mode-tts"
                className={`flex-1 rounded-md border px-2 py-1.5 text-xs transition-colors ${audioMode === 'tts' ? 'border-white bg-white text-black' : 'border-[#2A2A30] text-white/60 hover:text-white'}`}>
                Write a script (AI voice)
              </button>
            </div>

            {audioMode === 'upload' ? (
              <>
                <div className="mt-2 flex items-center gap-3">
                  <button onClick={() => audioRef.current?.click()} data-testid="avatar-audio-upload"
                    className={`flex h-11 shrink-0 items-center gap-2 rounded-lg border px-3 text-xs transition-colors ${audioDataUrl ? 'border-node-model/50 bg-node-model/10 text-white' : 'border-dashed border-[#2A2A30] text-white/40 hover:border-node-model hover:text-node-model'}`}>
                    <Music size={15} /> {audioName || 'Choose audio file'}
                  </button>
                  {audioDataUrl && (
                    <button onClick={() => { setAudioDataUrl(''); setAudioName(''); }} className="text-white/30 hover:text-red-400"><Trash2 size={14} /></button>
                  )}
                  <input ref={audioRef} type="file" accept="audio/*" className="hidden" onChange={onAudioPick} />
                </div>
                <p className="mt-1 text-[10px] leading-relaxed text-white/40">
                  What the avatar's mouth is synced to. Vocals are separated automatically, so background music in the clip is fine.
                </p>
              </>
            ) : (
              <>
                <textarea rows={3} value={script} onChange={(e) => setScript(e.target.value)}
                  placeholder="Exactly what the avatar should say, out loud..."
                  data-testid="avatar-script"
                  className="mt-2 w-full resize-none rounded-md border border-[#2A2A30] bg-[#0E0E10] p-2.5 text-sm text-white/85 focus:border-node-model focus:outline-none" />
                <p className="mt-1 text-[10px] leading-relaxed text-white/40">
                  Synthesized with Fish Audio, then lip-synced. This is the actual dialogue -- the scene description below shapes the picture, not the voice.
                </p>
              </>
            )}
          </div>

          <div>
            <label className="text-[11px] font-medium uppercase tracking-wider text-white/50">Reference photo (optional)</label>
            <div className="mt-1.5 flex items-center gap-3">
              {image ? (
                <div className="relative h-14 w-14 overflow-hidden rounded-lg border border-[#2A2A30]">
                  <img src={image} alt="" className="h-full w-full object-cover" />
                  <button onClick={() => setImage(null)} className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-white/80 hover:text-red-400"><Trash2 size={11} /></button>
                </div>
              ) : (
                <button onClick={() => imageRef.current?.click()} data-testid="avatar-image-upload"
                  className="flex h-14 w-14 shrink-0 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-[#2A2A30] text-white/40 transition-colors hover:border-node-model hover:text-node-model">
                  <ImagePlus size={16} /><span className="text-[9px]">Add</span>
                </button>
              )}
              <p className="text-[11px] leading-relaxed text-white/45">Who the avatar looks like. Without one, the text description alone frames the person.</p>
              <input ref={imageRef} type="file" accept="image/*" className="hidden" onChange={onImagePick} />
            </div>
          </div>

          <div>
            <label className="text-[11px] font-medium uppercase tracking-wider text-white/50">Describe the person / scene</label>
            <textarea rows={3} value={prompt} onChange={(e) => setPrompt(e.target.value)}
              data-testid="avatar-prompt"
              className="mt-1.5 w-full resize-none rounded-md border border-[#2A2A30] bg-[#0E0E10] p-2.5 text-sm text-white/85 focus:border-node-model focus:outline-none" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-medium uppercase tracking-wider text-white/50">Length · {numSegments} segment{numSegments > 1 ? 's' : ''}</label>
              <input type="range" min={1} max={maxSegments} step={1} value={numSegments}
                onChange={(e) => setNumSegments(parseInt(e.target.value))}
                data-testid="avatar-segments" className="mt-2 w-full accent-node-model" />
              <p className="mt-1 text-[10px] leading-relaxed text-white/35">
                Each extra segment continues the clip, chained from the last. Identity can drift a little over many segments.
              </p>
            </div>
            <div>
              <label className="text-[11px] font-medium uppercase tracking-wider text-white/50">Resolution</label>
              <div className="mt-1.5 flex gap-1.5">
                {RESOLUTIONS.map((r) => (
                  <button key={r.key} onClick={() => setResolution(r.key)} data-testid={`avatar-res-${r.key}`}
                    className={`flex-1 rounded-md border px-2 py-1.5 text-xs transition-colors ${resolution === r.key ? 'border-white bg-white text-black' : 'border-[#2A2A30] text-white/60 hover:text-white'}`}>
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <p className="text-[10px] leading-relaxed text-white/35">
            First run on a fresh worker can take several minutes; the model has to load. Once warm, later clips are quicker.
          </p>

          {busy && (
            <div className="mt-3 rounded-lg border border-[#2A2A30] bg-[#0E0E10] p-3" data-testid="avatar-progress">
              {progress?.total ? (
                <>
                  <div className="flex items-center justify-between text-[11px] text-white/60">
                    <span>{STAGE_LABELS[progress.stage] || 'Working'} · step {progress.step} of {progress.total}</span>
                    <span>~{Math.max(0, (progress.total - progress.step) * SECONDS_PER_STEP_ESTIMATE)}s left (rough estimate)</span>
                  </div>
                  <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                    <div className="h-full rounded-full bg-node-model transition-all"
                      style={{ width: `${Math.min(100, (progress.step / progress.total) * 100)}%` }} />
                  </div>
                </>
              ) : (
                <p className="flex items-center gap-1.5 text-[11px] text-white/50">
                  <Loader2 size={11} className="animate-spin" /> Starting the worker -- no progress reported yet, this can take a minute on a cold start.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-[#2A2A30] px-5 py-3.5">
          <button onClick={onClose} className="rounded-full border border-[#2A2A30] px-4 py-2 text-sm text-white/70 hover:text-white">Cancel</button>
          <button onClick={submit} disabled={busy || !canSubmit} data-testid="avatar-generate-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-white px-5 py-2 text-sm font-medium text-black hover:bg-white/90 disabled:opacity-50">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Mic size={15} />} Generate avatar video
          </button>
        </div>
      </div>
    </div>
  );
}
